import { FC, Fragment, useState } from 'react';
import {
  Button,
  Divider,
  Group,
  List,
  ListItem,
  NativeSelect as MNativeSelect,
  Textarea as MTextarea,
  TextInput as MTextInput,
  Text,
  ActionIcon,
  Stepper,
  Title,
  Accordion,
  Tabs,
} from '@mantine/core';
import { useConnectors } from 'utils/connectors';
import { Field, FieldArray, Form, Formik } from 'formik';
import { Trash } from 'tabler-icons-react';
import { showErrorNotification, showSuccessNotification } from 'utils/ui-toaster';
import { IntegrationPreview } from 'components/IntegrationsDashboard/IntegrationPreview';
import { TransformationEditor } from 'components/Editor/TransformationEditor';
import { useGetAllAPINames } from 'utils/dynamicSchema';
import TestTransformations from 'components/IntegrationsDashboard/TestingModules/TestTransformation';
import TestEndpoint from 'components/IntegrationsDashboard/TestingModules/TestEndpoints';
import VisualTransformation from 'components/IntegrationsDashboard/Visual_transformation/VisualTransformation';

type IProps = {
  onSubmit: (api: any) => Promise<any>;
} & (
  | { action: 'CREATE' }
  | {
      action: 'UPDATE';
      integration: any;
    }
);

export enum EndPointLabel {
  SETUP = 'Setup endpoint',
  TEST = 'Test endpoint',
  VISUAL_TRANS = 'Visual Transformation',
}

export enum ButtonLabel {
  ENDPOINTS = 'Endpoints',
  NEXTSTEP = 'Next Step',
  SKIP = 'Skip',
  BACK = 'Back',
  ADD_ADDITIONAL_RESOLVER_OP = 'Add Additional Resolver (Optional)',
  ADD_ADDITIONAL_RESOLVER = 'Add Additional Resolver',
  SUBMIT = 'Submit',
  ENDPOINTS_VARIABLE = "Endpoint's Variables",
  ADD_VARIABLE = 'Add Variable',
  ADD_ENDPOINT = '+ Add Endpoint',
}

export const newEndpointFields = {
  connectorId: '',
  uri: '',
  method: '',
  variables: [],
};

export const newVariableFields = {
  type: '',
  dataType: '',
  name: '',
  value: '',
};

export const newAdditionalResolverFields = {
  type: '',
  transformation: '',
  endpoints: [newEndpointFields],
};

const transforAdditionalEndpointstoTestingCompatible = (endpoints, endpointIndex = 0) => {
  return {
    endpoints: endpoints?.length
      ? endpoints.map(transformEndpointToFormFields)
      : [newEndpointFields],
    endpointIndex: endpointIndex ?? 0,
  };
};

const transformArrayHelperToEndPoints = (arrayHelper, endpointIndex = 0) => {
  return {
    name: arrayHelper?.form?.values.name ?? '',
    endpoints: arrayHelper?.form?.values.endpoints?.length
      ? arrayHelper?.form?.values.endpoints.map(transformEndpointToFormFields)
      : [newEndpointFields],
    endpointIndex: endpointIndex ?? 0,
    status: arrayHelper?.form?.values.status ?? '',
  };
};

const transformApiDetailsToFormFields = apiDetails => {
  return {
    name: apiDetails?.name ?? '',
    type: 'GraphQL',
    transformation: apiDetails?.transformation ?? '',
    endpoints: apiDetails?.endpoints?.length
      ? apiDetails.endpoints.map(transformEndpointToFormFields)
      : [newEndpointFields],
    status: 'Active',
    description: apiDetails?.description ?? '',
  };
};

const transformAdditionalResolversToFormFields = additionalResolvers => {
  return {
    additionalResolvers: additionalResolvers?.length
      ? additionalResolvers.map(resolver => ({
          type: resolver?.type ?? '',
          transformation: resolver?.transformation ?? '',
          endpoints: resolver?.endpoints?.length
            ? resolver.endpoints.map(transformEndpointToFormFields)
            : [newEndpointFields],
        }))
      : [],
  };
};

const transformEndpointToFormFields = endpoint => {
  return {
    connectorId: endpoint?.connectorId ?? '',
    uri: endpoint?.uri ?? '',
    method: endpoint?.method ?? '',
    variables: endpoint?.variables?.length
      ? endpoint.variables.map(transformVariableToFormFields)
      : [],
  };
};

const transformVariableToFormFields = variable => {
  return {
    type: variable?.type ?? '',
    dataType: variable?.dataType ?? '',
    name: variable?.name ?? '',
    value: variable?.value ?? '',
  };
};

export const DisableCheckForTestEndpoint = (label: string) => {
  return label === EndPointLabel.SETUP ? false : true;
};

const TextInput = ({ name, ...restProps }) => {
  return <Field name={name}>{({ field }) => <MTextInput {...field} {...restProps} />}</Field>;
};

const NativeSelect = ({ name, data, ...restProps }) => {
  return (
    <Field as="select" name={name}>
      {({ field, form }) => {
        const handleChange = event => {
          form.setFieldValue(field.name, event.currentTarget.value);
        };

        return <MNativeSelect {...field} data={data} onChange={handleChange} {...restProps} />;
      }}
    </Field>
  );
};

const Textarea = ({ name, ...restProps }) => {
  return <Field name={name}>{({ field }) => <MTextarea {...field} {...restProps} />}</Field>;
};

const IntegrationForm: FC<IProps> = props => {
  const integration = props.action === 'UPDATE' ? props.integration : {};
  const { additionalResolvers: initialAdditionalResolvers, ...initialApiDetails } = integration;
  const [isLoading, setIsLoading] = useState(false);
  const { data: allApiNames } = useGetAllAPINames();
  const { data: connectorsData } = useConnectors();
  const connectors = connectorsData?.Connectors?.edges;
  const [active, setActive] = useState(0);

  const [apiDetails, setApiDetails] = useState(props.action === 'UPDATE' ? initialApiDetails : {});
  const [additionalResolvers, setAdditionalResolvers] = useState(
    props.action === 'UPDATE' ? initialAdditionalResolvers : [],
  );

  const localStorageEnable = localStorage.getItem('enableVisualTransformation');
  return (
    <Fragment>
      <Stepper color="dark" active={active} onStepClick={setActive}>
        <Stepper.Step
          color="dark"
          label="Step 1"
          description="Add API Details"
          allowStepSelect={false}
        >
          <Formik
            initialValues={transformApiDetailsToFormFields(apiDetails)}
            onSubmit={values => {
              setApiDetails(values);
              setActive(s => s + 1);
            }}
            render={({ values }) => (
              <Form>
                {/* <NativeSelect
                  name="name"
                  label="name"
                  placeholder="API Name"
                  data={
                    allApiNames?.Apis?.edges?.map(e => ({
                      value: e.node.name,
                      label: e.node.name,
                    })) ?? []
                  }
                  required
                /> */}
                <TextInput name="name" label="name" placeholder="API Name" required />

                <TransformationEditor
                  placeholder="API Transformation"
                  name="transformation"
                  required
                />
                <Accordion iconPosition="right" style={{ margin: '15px 0px' }}>
                  <Accordion.Item label="Test Transfomation">
                    <TestTransformations transformation={values.transformation} />
                  </Accordion.Item>
                </Accordion>
                <Textarea name="description" label="Description" placeholder="API Description" />
                <List listStyleType="none" withPadding>
                  <Text weight="bold" size="sm" style={{ padding: '8px 0px' }}>
                    {ButtonLabel.ENDPOINTS}
                  </Text>
                  <Tabs>
                    <Tabs.Tab label="Setup endpoint">
                      <EndpointsFieldArray
                        name={`endpoints`}
                        connectors={connectors}
                        endpoints={values.endpoints}
                        label={EndPointLabel.SETUP}
                      />
                    </Tabs.Tab>
                    <Tabs.Tab label="Test endpoint">
                      <EndpointsFieldArray
                        name={`endpoints`}
                        connectors={connectors}
                        endpoints={values.endpoints}
                        label={EndPointLabel.TEST}
                      />
                    </Tabs.Tab>
                    {localStorageEnable == 'true' ? (
                      <Tabs.Tab label="Visual Transformation">
                        <EndpointsFieldArray
                          name={`endpoints`}
                          connectors={connectors}
                          endpoints={values.endpoints}
                          label={EndPointLabel.VISUAL_TRANS}
                        />
                      </Tabs.Tab>
                    ) : null}
                  </Tabs>
                </List>
                <Group>
                  <Button style={{ margin: '20px auto' }} color="dark" type="submit">
                    {ButtonLabel.NEXTSTEP}
                  </Button>
                </Group>
              </Form>
            )}
          />
        </Stepper.Step>
        <Stepper.Step
          color="dark"
          label="Step 2"
          description="Add Additional Resolvers"
          allowStepSelect={false}
        >
          <Title style={{ textAlign: 'center' }} order={4}>
            {ButtonLabel.ADD_ADDITIONAL_RESOLVER_OP}
          </Title>

          <Formik
            initialValues={transformAdditionalResolversToFormFields(additionalResolvers)}
            onSubmit={values => {
              setAdditionalResolvers(values.additionalResolvers);
              setActive(s => s + 1);
            }}
            render={({ values }) => (
              <Form>
                <FieldArray
                  name="additionalResolvers"
                  render={arrayHelpers => {
                    const additionalResolvers = values.additionalResolvers;
                    return (
                      <List withPadding listStyleType="none">
                        {additionalResolvers.length
                          ? additionalResolvers.map((additionResolver, resolverIndex) => (
                              <ListItem>
                                <ActionIcon
                                  onClick={() => arrayHelpers.remove(resolverIndex)}
                                  color="red"
                                  variant="outline"
                                >
                                  <Trash size={16} />
                                </ActionIcon>
                                <TextInput
                                  name={`additionalResolvers[${resolverIndex}].type`}
                                  label="Type"
                                  required
                                  placeholder="Resolver Type"
                                />

                                <TransformationEditor
                                  placeholder="API Transformation"
                                  name={`additionalResolvers[${resolverIndex}].transformation`}
                                  required
                                />
                                <Accordion iconPosition="right" style={{ margin: '15px 0px' }}>
                                  <Accordion.Item label="Test Transfomation">
                                    <TestTransformations
                                      transformation={additionResolver.transformation}
                                    />
                                  </Accordion.Item>
                                </Accordion>

                                <List listStyleType="none" withPadding>
                                  <Text weight="bold" size="sm" style={{ padding: '8px 0px' }}>
                                    {ButtonLabel.ENDPOINTS}
                                  </Text>
                                  <Tabs>
                                    <Tabs.Tab label="Setup endpoint">
                                      <EndpointsFieldArray
                                        name={`additionalResolvers[${resolverIndex}].endpoints`}
                                        connectors={connectors}
                                        endpoints={additionalResolvers[resolverIndex].endpoints}
                                        label={EndPointLabel.SETUP}
                                      />
                                    </Tabs.Tab>
                                    <Tabs.Tab label="Test endpoint">
                                      <EndpointsFieldArray
                                        name={`additionalResolvers[${resolverIndex}].endpoints`}
                                        connectors={connectors}
                                        endpoints={additionalResolvers[resolverIndex].endpoints}
                                        label={EndPointLabel.TEST}
                                      />
                                    </Tabs.Tab>
                                  </Tabs>
                                </List>

                                <Divider style={{ margin: '25px 0' }} />
                              </ListItem>
                            ))
                          : null}

                        <Group style={{ margin: '40px 0px' }}>
                          <Button
                            style={{ margin: '20px 0px' }}
                            color="dark"
                            onClick={() => arrayHelpers.push(newAdditionalResolverFields)}
                          >
                            {values.additionalResolvers.length > 0 ? '+' : ''}
                            {ButtonLabel.ADD_ADDITIONAL_RESOLVER}
                          </Button>

                          <Group style={{ marginLeft: 'auto' }}>
                            <Button
                              color="dark"
                              variant="light"
                              onClick={() => setActive(s => s - 1)}
                            >
                              {ButtonLabel.BACK}
                            </Button>
                            {values.additionalResolvers.length ? (
                              <Button color="dark" type="submit">
                                {ButtonLabel.NEXTSTEP}
                              </Button>
                            ) : (
                              <>
                                <Button color="dark" variant="outline" type="submit">
                                  {ButtonLabel.SKIP}
                                </Button>
                              </>
                            )}
                          </Group>
                        </Group>
                      </List>
                    );
                  }}
                />
              </Form>
            )}
          />
        </Stepper.Step>
        <Stepper.Step
          color="dark"
          label="Step 3"
          description={`Review and ${props.action === 'CREATE' ? 'Create' : 'Update'}`}
          allowStepSelect={false}
        >
          <IntegrationPreview setActive={setActive} api={{ ...apiDetails, additionalResolvers }} />
          <Group style={{ display: 'flex', justifyContent: 'center', margin: '100px auto' }}>
            <Button color="dark" variant="light" onClick={() => setActive(s => s - 1)}>
              {ButtonLabel.BACK}
            </Button>
            <Button
              loading={isLoading}
              color="dark"
              onClick={() => {
                setIsLoading(true);
                props
                  .onSubmit({ ...apiDetails, additionalResolvers })
                  .then(() => {
                    showSuccessNotification(
                      `API was ${props.action === 'UPDATE' ? 'updated' : 'created'} successfully.`,
                    );
                    if (props.action === 'CREATE') {
                      setApiDetails({});
                      setAdditionalResolvers([]);
                    }

                    setActive(0);
                  })
                  .catch(err => {
                    showErrorNotification(
                      `${err?.graphQLErrors?.[0]?.description}.   ${
                        err?.graphQLErrors?.[0]?.additionalInfo?.message
                      }. \n Hence failed to ${
                        props.action === 'UPDATE' ? 'update' : 'create'
                      } the API.`,
                    );
                  })
                  .finally(() => {
                    setIsLoading(false);
                  });
              }}
            >
              {ButtonLabel.SUBMIT}
            </Button>
          </Group>
        </Stepper.Step>
      </Stepper>
    </Fragment>
  );
};

export default IntegrationForm;

const EndpointsFieldArray = props => {
  const { name, connectors = [], endpoints = [] } = props;

  const dataMethod = [
    { value: 'GET', label: 'GET' },
    { value: 'POST', label: 'POST' },
    { value: 'DELETE', label: 'DELETE' },
    { value: 'PATCH', label: 'PATCH' },
  ];

  const dataVariableType = [
    {
      value: 'QUERY',
      label: 'QUERY',
    },
    { value: 'PATH', label: 'PATH' },
    { value: 'BODY', label: 'BODY' },
  ];

  const dataDataType = [
    {
      value: 'STRING',
      label: 'STRING',
    },
    {
      value: 'INTEGER',
      label: 'INTEGER',
    },
    { value: 'LIST', label: 'LIST' },
  ];

  return (
    <FieldArray
      name={`${name}`}
      render={arrayHelpers => (
        <ListItem>
          <List listStyleType="none" withPadding>
            {endpoints.map((endpoint, endpointIndex) => (
              <ListItem key={endpointIndex}>
                <ActionIcon
                  onClick={() => arrayHelpers.remove(endpointIndex)}
                  color="red"
                  variant="outline"
                  style={{
                    display: endpoints.length > 1 ? 'block' : 'none',
                  }}
                >
                  <Trash size={16} />
                </ActionIcon>
                {props.label !== EndPointLabel.TEST ? (
                  <Group grow>
                    {connectors?.length ? (
                      <NativeSelect
                        label="Connector"
                        placeholder="Select Connector"
                        name={`${name}[${endpointIndex}].connectorId`}
                        required
                        data={connectors.map(({ node }) => ({
                          value: node.id,
                          label: node.name,
                        }))}
                      />
                    ) : (
                      <TextInput
                        name={`${name}[${endpointIndex}].connectorId`}
                        label="Connector"
                        placeholder="Connector ID"
                        required
                      />
                    )}

                    <TextInput
                      label="URI"
                      placeholder="Endpoint URI"
                      name={`${name}[${endpointIndex}].uri`}
                      required
                    />
                  </Group>
                ) : null}

                {props.label !== EndPointLabel.TEST ? (
                  <NativeSelect
                    name={`${name}[${endpointIndex}].method`}
                    label="Method"
                    placeholder="Method Type"
                    data={dataMethod}
                    required
                  />
                ) : null}
                <Text weight="bold" size="sm" style={{ padding: '8px 0px' }}>
                  {ButtonLabel.ENDPOINTS_VARIABLE}
                </Text>
                <List withPadding listStyleType="none">
                  <FieldArray
                    name={`${name}[${endpointIndex}].variables`}
                    render={arrayHelpers => (
                      <Fragment>
                        {endpoints[endpointIndex].variables.map((variable, variableIndex) => (
                          <ListItem style={{ margin: '20px 0px' }}>
                            <Group>
                              <TextInput
                                name={`${name}[${endpointIndex}].variables[${variableIndex}].name`}
                                label="Variable Name"
                                disabled={DisableCheckForTestEndpoint(props.label)}
                                sx={{ flex: 0.5 }}
                              />

                              <NativeSelect
                                name={`${name}[${endpointIndex}].variables[${variableIndex}].type`}
                                label="Variable Type"
                                placeholder="Variable Type"
                                disabled={DisableCheckForTestEndpoint(props.label)}
                                data={dataVariableType}
                                sx={{ flex: 0.25 }}
                                required
                              />
                              <NativeSelect
                                name={`${name}[${endpointIndex}].variables[${variableIndex}].dataType`}
                                label="Variable DataType"
                                placeholder="Variable DataType"
                                disabled={DisableCheckForTestEndpoint(props.label)}
                                data={dataDataType}
                                sx={{ flex: 0.25 }}
                                required
                              />

                              {props.label !== EndPointLabel.TEST ? (
                                <ActionIcon
                                  style={{
                                    display:
                                      endpoints[endpointIndex]?.variables?.length > 0
                                        ? 'block'
                                        : 'none',
                                  }}
                                  onClick={() => arrayHelpers.remove(variableIndex)}
                                  color="red"
                                  variant="outline"
                                >
                                  <Trash size={16} />
                                </ActionIcon>
                              ) : null}
                            </Group>
                            <Textarea
                              name={`${name}[${endpointIndex}].variables[${variableIndex}].value`}
                              label="Variable Value"
                              sx={{ flex: 1 }}
                              required
                            />
                          </ListItem>
                        ))}
                        {props.label !== EndPointLabel.TEST ? (
                          <Button
                            color="dark"
                            style={{ margin: '20px 0px' }}
                            onClick={() => arrayHelpers.push(newVariableFields)}
                            size="xs"
                          >
                            {ButtonLabel.ADD_VARIABLE}
                          </Button>
                        ) : null}
                        {props.label === EndPointLabel.TEST ? (
                          <TestEndpoint
                            data={
                              name.includes('additional')
                                ? transforAdditionalEndpointstoTestingCompatible(
                                    endpoints,
                                    endpointIndex,
                                  )
                                : transformArrayHelperToEndPoints(arrayHelpers, endpointIndex)
                            }
                          />
                        ) : props.label === EndPointLabel.SETUP ? null : (
                          <VisualTransformation
                            data={
                              name.includes('additional')
                                ? transforAdditionalEndpointstoTestingCompatible(
                                    endpoints,
                                    endpointIndex,
                                  )
                                : transformArrayHelperToEndPoints(arrayHelpers, endpointIndex)
                            }
                          />
                        )}
                      </Fragment>
                    )}
                  />
                </List>

                <Divider style={{ margin: '25px 0px' }} />
              </ListItem>
            ))}
            {props?.label === EndPointLabel.SETUP ? (
              <Button color="dark" size="xs" onClick={() => arrayHelpers.push(newEndpointFields)}>
                {ButtonLabel.ADD_ENDPOINT}
              </Button>
            ) : null}
          </List>
        </ListItem>
      )}
    />
  );
};
